import { useNavigate, useParams } from 'react-router-dom';
import { TooltipProvider } from '@design-system';
import {
  usePolicyOptions,
  useProjectionForPolicy,
  usePolicyById,
  useClientLoadStatus,
} from './hooks/useProjectionForPolicy';
import { useRedirectToDefaultPolicy } from './hooks/useRedirectToDefaultPolicy';
import { PolicyPicker } from './components/PolicyPicker';
import { PolicyDetailsCard } from './components/PolicyDetailsCard';
import { ReturnsSummaryCard } from './components/ReturnsSummaryCard';
import { MonthlyBreakdownTable } from './components/MonthlyBreakdownTable';
import { IdentityChecksPanel } from './components/IdentityChecksPanel';
import { EmptyState } from './components/EmptyState';

export function ProjectionDetailsPage() {
  const navigate = useNavigate();
  const { clientId, policyId } = useParams<{ clientId: string; policyId?: string }>();
  const { loaded, clientFullName } = useClientLoadStatus();
  const policyOptions = usePolicyOptions();
  const projection = useProjectionForPolicy(policyId);
  const policy = usePolicyById(policyId);

  useRedirectToDefaultPolicy(clientId, policyId, policyOptions);

  if (!loaded) {
    return (
      <div className="mx-auto max-w-[1600px] p-6">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="mx-auto max-w-[1600px] space-y-4 p-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Projection details</h1>
            <p className="text-sm text-gray-500">
              {clientFullName} — month-by-month claim projection
            </p>
          </div>
          <PolicyPicker
            options={policyOptions}
            value={policyId}
            onChange={id => navigate(`/clients/${clientId}/projection-details/${id}`)}
          />
        </header>

        {policyOptions.length === 0 ? (
          <EmptyState
            title="No LTC policies on this client"
            body="Add an LTC policy as a funding source to see projection details."
          />
        ) : !projection ? (
          <EmptyState
            title="No projection data yet"
            body="Run a calculation on this client to populate the projection details."
          />
        ) : (
          <div className="space-y-3">
            <PolicyDetailsCard policy={policy} />
            <ReturnsSummaryCard projection={projection} />
            <MonthlyBreakdownTable projection={projection} policyType={policy?.policyType} />
            <IdentityChecksPanel projection={projection} />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
